import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  TrendingUp,
  Plus,
  CreditCard,
  X,
  Settings,
  DollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  PiggyBank,
  ShoppingCart,
  Sparkles,
} from "lucide-react";

export default function MobileNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAddModal, setShowAddModal] = useState(false);

  const routes = [
    { path: "/dashboard", label: "Dashboard", icon: Home },
    { path: "/investments", label: "Inversiones", icon: TrendingUp },
    { path: "/subscriptions", label: "Suscripciones", icon: CreditCard },
    { path: "/profile", label: "Settings", icon: Settings },
  ];

  const getCurrentActiveIndex = useCallback(() => {
    return routes.findIndex((route) => route.path === location.pathname);
  }, [location.pathname, routes]);

  const handleAddClick = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleAddExpense = useCallback(() => {
    setShowAddModal(false);
    navigate("/expenses");
  }, [navigate]);

  const handleAddIncome = useCallback(() => {
    setShowAddModal(false);
    navigate("/incomes");
  }, [navigate]);

  const closeModal = useCallback(() => {
    setShowAddModal(false);
  }, []);

  const handleNavigation = useCallback(
    (path) => {
      navigate(path);
    },
    [navigate]
  );

  // Animation variants
  const modalVariants = {
    hidden: {
      y: "100%",
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.8,
        opacity: { duration: 0.2 },
      },
    },
    exit: {
      y: "100%",
      opacity: 0,
      scale: 0.95,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.5,
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.25 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const buttonVariants = {
    tap: { scale: 0.92 },
    hover: { scale: 1.05 },
  };

  const activeIndex = getCurrentActiveIndex();

  return (
    <>
      {/* Modal Overlay */}
      <AnimatePresence mode="wait">
        {showAddModal && (
          <motion.div
            className="fixed inset-0 z-60 flex items-end justify-center"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={backdropVariants}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/30 backdrop-blur-md"
              onClick={closeModal}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal Content */}
            <motion.div
              className="relative bg-white rounded-t-3xl p-4 w-full max-w-sm mx-4 mb-20 shadow-2xl border border-gray-100 max-h-[50vh] overflow-y-auto"
              variants={modalVariants}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.1}
              onDragEnd={(event, info) => {
                if (info.offset.y > 100) {
                  closeModal();
                }
              }}
            >
              {/* Drag Handle */}
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4 opacity-60 cursor-grab active:cursor-grabbing" />

              {/* Close Button */}
              <motion.button
                onClick={closeModal}
                className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-4 h-4 text-gray-600" />
              </motion.button>

              {/* Header */}
              <div className="text-center mb-6">
                <motion.div
                  className="w-12 h-12 bg-gradient-to-br from-[#14840c] to-green-600 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg"
                  animate={{ rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <Sparkles className="w-6 h-6 text-white" />
                </motion.div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  ¿Qué vas a registrar?
                </h3>
                <p className="text-gray-500 text-xs">
                  Gestiona tus finanzas inteligentemente
                </p>
              </div>

              {/* Options */}
              <div className="space-y-3 mb-4">
                {/* Add Income */}
                <motion.button
                  onClick={handleAddIncome}
                  className="w-full group relative overflow-hidden bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border border-green-200 hover:border-green-300 rounded-xl p-3 transition-all duration-300"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <motion.div
                        className="w-10 h-10 bg-gradient-to-br from-[#14840c] to-green-600 rounded-lg flex items-center justify-center shadow-md"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        <ArrowUpCircle className="w-5 h-5 text-white" />
                      </motion.div>
                      <motion.div
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full"
                        animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                    </div>
                    <div className="text-left flex-1">
                      <h4 className="font-bold text-sm text-gray-900">
                        Nuevo Ingreso
                      </h4>
                      <p className="text-xs text-green-600 font-medium">
                        Dinero que entra
                      </p>
                    </div>
                    <motion.div
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ x: -10 }}
                      whileHover={{ x: 0 }}
                    >
                      <PiggyBank className="w-4 h-4 text-green-500" />
                    </motion.div>
                  </div>
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400/0 via-green-400/5 to-green-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                </motion.button>

                {/* Add Expense */}
                <motion.button
                  onClick={handleAddExpense}
                  className="w-full group relative overflow-hidden bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 border border-red-200 hover:border-red-300 rounded-xl p-3 transition-all duration-300"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <motion.div
                        className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-md"
                        whileHover={{ scale: 1.1, rotate: -5 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                        }}
                      >
                        <ArrowDownCircle className="w-5 h-5 text-white" />
                      </motion.div>
                      <motion.div
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-400 rounded-full"
                        animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          delay: 0.5,
                        }}
                      />
                    </div>
                    <div className="text-left flex-1">
                      <h4 className="font-bold text-sm text-gray-900">
                        Nuevo Gasto
                      </h4>
                      <p className="text-xs text-red-600 font-medium">
                        Dinero que sale
                      </p>
                    </div>
                    <motion.div
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      initial={{ x: -10 }}
                      whileHover={{ x: 0 }}
                    >
                      <ShoppingCart className="w-4 h-4 text-red-500" />
                    </motion.div>
                  </div>
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-red-400/0 via-red-400/5 to-red-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                </motion.button>
              </div>

              {/* Quick Stats */}
              <motion.div
                className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg p-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-xs font-medium">
                    Control financiero inteligente
                  </span>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Navbar */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        {/* Navbar Container */}
        <div className="relative bg-white/95 backdrop-blur-xl shadow-2xl rounded-t-3xl border-t border-gray-200">
          {/* Center Add Button - Más compacto */}
          <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
            <motion.button
              onClick={handleAddClick}
              className={`bg-gradient-to-r from-[#14840c] to-green-600 hover:from-green-600 hover:to-green-700 w-12 h-12 rounded-full flex items-center justify-center shadow-xl border-3 border-white transition-all duration-300 ${
                showAddModal ? "ring-2 ring-green-300 ring-offset-1" : ""
              }`}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              animate={showAddModal ? { rotate: 45 } : { rotate: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <motion.div
                animate={showAddModal ? { rotate: 45 } : { rotate: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <Plus className="w-6 h-6 text-white" />
              </motion.div>
            </motion.button>
          </div>

          {/* Navigation Items - Más compacto */}
          <div className="grid grid-cols-4 gap-0.5 px-3 pt-6 pb-3">
            {routes.map((route, index) => {
              const isActive = activeIndex === index;
              const Icon = route.icon;

              return (
                <motion.button
                  key={route.path}
                  onClick={() => handleNavigation(route.path)}
                  className={`flex flex-col items-center justify-center text-xs font-medium transition-all duration-200 py-1.5 px-1 rounded-lg ${
                    isActive
                      ? "text-[#14840c] bg-green-50"
                      : "text-gray-500 hover:text-[#14840c] hover:bg-green-50/50"
                  }`}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  layout
                >
                  <motion.div
                    className={`p-1.5 rounded-md transition-all duration-200 ${
                      isActive ? "bg-green-100 shadow-sm" : ""
                    }`}
                    animate={isActive ? { scale: 1.05 } : { scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Icon className="w-4 h-4" />
                  </motion.div>
                  <motion.span
                    className={`mt-0.5 text-center leading-tight text-xs ${
                      isActive ? "font-semibold" : "font-medium"
                    }`}
                    animate={isActive ? { y: -0.5 } : { y: 0 }}
                  >
                    {route.label}
                  </motion.span>
                  {/* Active indicator */}
                  {isActive && (
                    <motion.div
                      className="absolute bottom-0.5 w-1 h-0.5 bg-[#14840c] rounded-full"
                      layoutId="activeIndicator"
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                      }}
                    />
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Safe Area minimalista */}
          <div className="h-1" />
        </div>
      </div>

      {/* Bottom spacer compacto */}
      <div className="h-16 lg:h-0" />
    </>
  );
}
